import {
  CAPTURE_STAGES,
  FULL_JOURNEY_STEPS,
  PROCESS_MIN,
  SHOT_CHECKS,
} from '../captureFlowGuide';

/** Live checklist — guidance only (ticks after each clip is saved). */
export function CaptureShotChecklist({ checks, active }) {
  if (!active) return null;
  return (
    <ul className="shot-checklist" aria-label="Shot checklist">
      {SHOT_CHECKS.map((item) => {
        const ok = Boolean(checks?.[item.id]);
        return (
          <li key={item.id} className={`shot-check${ok ? ' ok' : ''}`}>
            <span className="shot-check-mark" aria-hidden="true">
              {ok ? '✓' : '○'}
            </span>
            {item.label}
          </li>
        );
      })}
    </ul>
  );
}

/** Compact CTA — opens stage clip preview in a modal (keeps capture layout clean). */
export function CapturePreviewCta({ label = 'View preview', count = 0, onClick, disabled }) {
  if (!count) return null;
  return (
    <button
      type="button"
      className="capture-preview-cta"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
      {count > 1 ? <span className="capture-preview-cta__count">{count}</span> : null}
    </button>
  );
}

/** Modal popup to review saved stage clips. */
export function CapturePreviewModal({
  open,
  title = 'Clip preview',
  items,
  activeUrl,
  onSelect,
  onRetake,
  onClose,
  disabled,
}) {
  if (!open || !items?.length) return null;
  const playing = activeUrl || items[0]?.url || '';
  return (
    <div
      className="tip-modal-backdrop capture-preview-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="tip-modal capture-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="capture-preview-modal__head">
          <h2 id="capture-preview-title" className="tip-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="capture-preview-modal__close"
            onClick={onClose}
            aria-label="Close preview"
          >
            ×
          </button>
        </div>

        {items.length > 1 ? (
          <div className="capture-preview-modal__chips">
            {items.map((item) => (
              <div key={item.id} className="capture-preview-modal__chip-wrap">
                <button
                  type="button"
                  className={`capture-preview-modal__chip${playing === item.url ? ' on' : ''}`}
                  disabled={disabled || !item.url}
                  onClick={() => onSelect?.(item.url)}
                >
                  {item.label}
                </button>
                {typeof item.sequence === 'number' && typeof onRetake === 'function' ? (
                  <button
                    type="button"
                    className="capture-preview-modal__retake"
                    disabled={disabled}
                    onClick={() => {
                      onRetake(item.sequence);
                      onClose?.();
                    }}
                  >
                    Retake
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {playing ? (
          <video
            key={playing}
            className="capture-preview-modal__player"
            src={playing}
            controls
            playsInline
            autoPlay
            preload="metadata"
          />
        ) : (
          <p className="tip-modal-body">No preview available.</p>
        )}

        <div className="capture-preview-modal__actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** Process clip retake row — tap a clip number to re-record it. */
export function ProcessClipRetakeRow({ clips, retakeSeq, onRetake, disabled }) {
  if (!clips?.length) return null;
  return (
    <div className="process-retake-row" aria-label="Retake a process clip">
      <span className="process-retake-label">Retake clip:</span>
      {clips.map((clip) => (
        <button
          key={clip.sequence}
          type="button"
          className={`process-retake-chip${retakeSeq === clip.sequence ? ' on' : ''}`}
          disabled={disabled}
          onClick={() => onRetake(clip.sequence)}
        >
          {clip.sequence + 1}
        </button>
      ))}
    </div>
  );
}

/** Full-screen intro / help — what to film and which buttons to tap. */
export function CaptureFlowGuideModal({ onClose, title = 'How to make your reel' }) {
  return (
    <div className="tip-modal-backdrop" role="presentation">
      <div
        className="tip-modal tip-modal--flow"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-flow-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="eyebrow">Quick guide</p>
        <h2 id="capture-flow-title" className="tip-modal-title">
          {title}
        </h2>
        <p className="tip-modal-body flow-guide-lede">
          Record Before, During (add as many clips as you need), and After. The app turns them into one vertical reel.
        </p>

        <div className="flow-guide-cards">
          {CAPTURE_STAGES.map((stageItem) => (
            <article key={stageItem.id} className="flow-guide-card">
              <span className="flow-guide-num" aria-hidden="true">
                {stageItem.step}
              </span>
              <div className="flow-guide-card-body">
                <h3>{stageItem.shortLabel}</h3>
                <p className="flow-guide-film">{stageItem.filmWhat}</p>
                <p className="flow-guide-tap">{stageItem.tapWhat}</p>
                <p className="flow-guide-time">
                  {stageItem.id === 'process'
                    ? `${stageItem.seconds}s each · add as needed`
                    : `About ${stageItem.seconds}s`}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="flow-guide-after panel">
          <p className="flow-guide-after-title">Then what happens?</p>
          <ol className="flow-guide-journey">
            {FULL_JOURNEY_STEPS.map((item) => (
              <li key={item.step}>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
              </li>
            ))}
          </ol>
        </div>

        <p className="tip-modal-body tip-do">
          Use the gold <strong>Start recording</strong> button on the camera. On During, finish with{' '}
          <strong>Done with process</strong>; on Before/After use <strong>Next step</strong> /{' '}
          <strong>Create reel</strong>.
        </p>
        <button type="button" className="btn btn-primary tip-modal-close" onClick={onClose}>
          Got it — start recording
        </button>
      </div>
    </div>
  );
}

/** Per-step tip when entering a new stage. */
export function CaptureStageTipModal({ stage, onClose, voiceSupported }) {
  return (
    <div className="tip-modal-backdrop" role="presentation">
      <div
        className="tip-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-tip-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="eyebrow">
          Step {stage.step} of {CAPTURE_STAGES.length}
        </p>
        <h2 id="capture-tip-title" className="tip-modal-title">
          {stage.tipTitle}
        </h2>
        <div className="flow-guide-card flow-guide-card--inline">
          <span className="flow-guide-num" aria-hidden="true">
            {stage.step}
          </span>
          <div className="flow-guide-card-body">
            <p className="flow-guide-film">{stage.filmWhat}</p>
            <p className="flow-guide-tap">{stage.tapWhat}</p>
          </div>
        </div>
        <p className="tip-modal-body">{stage.tipBody}</p>
        <p className="tip-modal-meta">About {stage.seconds} seconds</p>
        {voiceSupported ? (
          <p className="tip-modal-body tip-voice-note">
            Optional: tap <strong>Mic</strong> for hands-free voice control.
          </p>
        ) : null}
        <button type="button" className="btn btn-primary tip-modal-close" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}

/** Persistent step progress — current stage highlighted. */
export function CaptureFlowRail({ stageIndex, localStatus }) {
  return (
    <div className="capture-flow-rail" aria-label="Recording progress">
      {CAPTURE_STAGES.map((stageItem, index) => {
        const done =
          stageItem.id === 'process'
            ? localStatus.process >= PROCESS_MIN
            : Boolean(localStatus[stageItem.id]);
        const active = index === stageIndex;
        const processNote =
          stageItem.id === 'process' && localStatus.process > 0
            ? ` (${localStatus.process})`
            : '';
        return (
          <div
            key={stageItem.id}
            className={`capture-flow-rail-item${active ? ' on' : ''}${done ? ' done' : ''}`}
          >
            <span className="capture-flow-rail-num">{done ? '✓' : stageItem.step}</span>
            <span className="capture-flow-rail-label">
              {stageItem.shortLabel}
              {processNote}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Home / service select — compact numbered guide. */
export function ReelsFlowGuidePanel({ compact = false }) {
  return (
    <div className={`reels-flow-guide panel${compact ? ' reels-flow-guide--compact' : ''}`}>
      <p className="reels-flow-guide-title">How it works</p>
      <ol className="reels-flow-guide-list">
        {CAPTURE_STAGES.map((stageItem) => (
          <li key={stageItem.id}>
            <span className="reels-flow-guide-step">{stageItem.step}</span>
            <div>
              <strong>{stageItem.label}</strong>
              <span>{stageItem.filmWhat}</span>
            </div>
          </li>
        ))}
        <li className="reels-flow-guide-finish">
          <span className="reels-flow-guide-step">✓</span>
          <div>
            <strong>Get your reel</strong>
            <span>App builds the video — download from Preview</span>
          </div>
        </li>
      </ol>
    </div>
  );
}
